import * as React from 'react';
import {useRef, useState, useCallback} from 'react';
import {createView, view, AnimatePresence} from '@sail/engine';
import {useId} from '@sail/react-aria';
import {tokens as $} from '@sail/tokens';
import type {PopoverInternalProps} from './utils';
import {BottomSheet} from '../bottom-sheet/BottomSheet';
import {PopoverBottomSheetHeader} from './PopoverBottomSheetHeader';
import {ButtonConfig} from '../Button';
import {SelectConfig} from '../Select';
import {SelectFieldConfig} from '../SelectFieldConfig';
import {TextFieldConfig} from '../TextField';
import {useProps} from '../../hooks/useProps';
import {css} from '../../css';

export const PopoverBottomSheet = createView<PopoverInternalProps>(
  ({
    popoverIsControlled,
    open,
    onOpenChange,
    targetRef,
    placement: reactAriaPlacement,
    offset: offsetOptions,
    flip: flipEnabled,
    shift: shouldShift,
    size: sizeOptions,
    inline: inlineEnabled,
    hide: hideOptions,
    subviews,
    dismissible,
    showOnPress,
    showOnFocus,
    showOnHover,
    mobileTitle,
    title,
    floatingStrategy,
    animate: shouldAnimate = true,
    disableDefaultBottomSheetHeader = false,
    disableDefaultBottomSheetScrollWrapper = false,
    useReactAriaDismissOutside,
    children,
    // We don't want to spread inherits to the Popover mobile, as people
    // will be thinking about the desktop version of the Popover when they
    // pass them. If we ever want to allow bottom sheet customization,
    // we can then expose a subview.
    inherits,
    ...props
  }) => {
    // We ignore showOnPress, showOnFocus, showOnHover, since on mobile there is no hover.
    // It would be worth looking into supporting hover for accessibility in the future.
    useProps(targetRef, {
      onClick: () => {
        if (!popoverIsControlled) {
          onOpenChange(true);
        }
      },
    });

    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const scrollWrapperRef = useRef<HTMLDivElement>(null);
    const [isScrolled, setIsScrolled] = useState(false);

    const handleScroll = useCallback(() => {
      const element = scrollWrapperRef.current;
      if (!element) {
        return;
      }
      // eslint-disable-next-line @sail/ui-no-reflow
      setIsScrolled(element.scrollTop > 0);
    }, []);

    const hasTitle = !!(mobileTitle || title);
    const headerId = useId();

    const content = disableDefaultBottomSheetScrollWrapper ? (
      children
    ) : (
      <view.div
        ref={scrollWrapperRef}
        onScroll={handleScroll}
        css={{
          overflowY: 'auto',
          height: 'fill',
          stack: 'y',
        }}
      >
        {children}
      </view.div>
    );

    const layer = (
      <BottomSheet
        aria-labelledby={hasTitle ? headerId : undefined}
        {...props}
        contentWrapperRef={contentWrapperRef}
        requestClose={() => onOpenChange(false)}
        closeConfirmationDialog={null}
        closeConfirmationDialogHeight={null}
        layerName="modal"
        mobileDisplay="bottom-sheet"
        modal
        shouldAnimate={shouldAnimate}
        inherits={subviews.mobile}
        subviews={{layer: subviews.layer}}
        // Custom props for the content wrapper
        css={{stack: 'y'}}
        data-testid={props['data-testid'] ?? 'popover-layer'}
        uses={[
          ButtonConfig.customize({defaults: {size: 'large'}}),
          SelectConfig.customize({defaults: {size: 'large'}}),
          SelectFieldConfig.customize({defaults: {size: 'large'}}),
          TextFieldConfig.customize({defaults: {size: 'large'}}),
        ]}
      >
        {disableDefaultBottomSheetHeader && !mobileTitle ? null : (
          <PopoverBottomSheetHeader
            onClose={() => onOpenChange(false)}
            title={mobileTitle || title}
            titleId={headerId}
            css={
              isScrolled
                ? css({borderBlockEnd: `1px solid ${$['color.border']}`})
                : css({borderBlockEnd: 'none'})
            }
          />
        )}
        {content}
      </BottomSheet>
    );

    return <AnimatePresence>{open ? layer : null}</AnimatePresence>;
  },
);
